import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import WebScrapeTab from "./components/WebScrapeTab";
import rocketIcon from "./assets/icon/rocket.png";
import tickIcon from "./assets/icon/tick.png";
import locationIcon from "./assets/icon/location.png";
import calendarIcon from "./assets/icon/calendar.png";
import externalIcon from "./assets/icon/external.png";
import qnuSyncLogo from "./assets/QNU_Sync.png";
import qnuImage from "./assets/qnu.png";
import { API_BASE_URL } from "./config/api";
import "./App.css";

const GOOGLE_COLORS_HEX = {
  1: "#2f6ee5",
  2: "#2fb35d",
  3: "#767fd2",
  4: "#ff5a1f",
  5: "#f2ba18",
  6: "#00a7c4",
  7: "#7657ff",
  8: "#545b6b",
  9: "#b3a3ff",
  10: "#00887a",
  11: "#e67c73",
};

const dayOfWeekMap = {
  "chu nhat": 0,
  sunday: 0,
  "thu hai": 1,
  monday: 1,
  "thu ba": 2,
  tuesday: 2,
  "thu tu": 3,
  wednesday: 3,
  "thu nam": 4,
  thursday: 4,
  "thu sau": 5,
  friday: 5,
  "thu bay": 6,
  saturday: 6,
};

function normalizeText(value) {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function parseIsoDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date, amount) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
}

function getStartOfWeek(date) {
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  startDate.setDate(startDate.getDate() - startDate.getDay());
  return startDate;
}

function getCurrentWeekRange() {
  const startDate = getStartOfWeek(new Date());
  const endDate = addDays(startDate, 6);

  return {
    startDate: formatIsoDate(startDate),
    endDate: formatIsoDate(endDate),
  };
}

function getPreviewDateForDay(dayOfWeek) {
  const weekStart = getStartOfWeek(new Date());
  const dayIndex = dayOfWeekMap[normalizeText(dayOfWeek)] ?? 1;
  return formatIsoDate(addDays(weekStart, dayIndex));
}

function getFirstOccurrenceOnOrAfter(dayOfWeek, fromDate, limitDate) {
  const startDate = parseIsoDate(fromDate);
  const endDate = parseIsoDate(limitDate);

  if (!startDate || !endDate || startDate > endDate) {
    return "";
  }

  const dayIndex = dayOfWeekMap[normalizeText(dayOfWeek)] ?? 1;
  const nextDate = new Date(startDate);

  while (nextDate <= endDate && nextDate.getDay() !== dayIndex) {
    nextDate.setDate(nextDate.getDate() + 1);
  }

  return nextDate <= endDate ? formatIsoDate(nextDate) : "";
}

function isFileCourse(course) {
  return course?.sourceType === "file";
}

function buildScheduleKey(course, schedule, scheduleIndex) {
  return [
    course.courseId || course.courseName || "course",
    schedule.dayOfWeek || "day",
    schedule.startTime || "start",
    schedule.endTime || "end",
    schedule.startDate || "from",
    schedule.endDate || "to",
    schedule.room || schedule.classroom || schedule.location || "room",
    scheduleIndex,
  ].join("__");
}

function enrichCourses(courses) {
  return (courses || []).map((course, courseIndex) => ({
    ...course,
    __courseKey: `${course.courseId || course.courseName || "course"}__${courseIndex}`,
    schedules: (course.schedules || []).map((schedule, scheduleIndex) => ({
      ...schedule,
      __scheduleKey: buildScheduleKey(course, schedule, scheduleIndex),
      isDateInferred:
        isFileCourse(course) && (!schedule.startDate || !schedule.endDate),
      previewStartDate:
        isFileCourse(course) && (!schedule.startDate || !schedule.endDate)
          ? getPreviewDateForDay(schedule.dayOfWeek)
          : schedule.startDate,
      previewEndDate:
        isFileCourse(course) && (!schedule.startDate || !schedule.endDate)
          ? getPreviewDateForDay(schedule.dayOfWeek)
          : schedule.endDate,
    })),
  }));
}

function cloneCourses(courses) {
  return (courses || []).map((course) => ({
    ...course,
    schedules: (course.schedules || []).map((schedule) => ({ ...schedule })),
  }));
}

function stripPreviewMetadata(courses) {
  return (courses || []).map((course) => {
    const { __courseKey, sourceType, ...cleanCourse } = course;

    return {
      ...cleanCourse,
      schedules: (course.schedules || []).map((schedule) => {
        const {
          __scheduleKey,
          isDateInferred,
          previewStartDate,
          previewEndDate,
          ...cleanSchedule
        } = schedule;
        return cleanSchedule;
      }),
    };
  });
}

function countSchedules(courses) {
  return (courses || []).reduce(
    (total, course) => total + (course.schedules?.length || 0),
    0,
  );
}

function getDateBounds(courses) {
  let startDate = "";
  let endDate = "";

  for (const course of courses || []) {
    for (const schedule of course.schedules || []) {
      const scheduleStartDate = schedule.previewStartDate || schedule.startDate;
      const scheduleEndDate = schedule.previewEndDate || schedule.endDate;

      if (scheduleStartDate && (!startDate || scheduleStartDate < startDate)) {
        startDate = scheduleStartDate;
      }

      if (scheduleEndDate && (!endDate || scheduleEndDate > endDate)) {
        endDate = scheduleEndDate;
      }
    }
  }

  return { startDate, endDate };
}

function buildSyncPayload(courses, syncMode, syncStartDate, syncEndDate) {
  const nextCourses = (courses || [])
    .map((course) => ({
      ...course,
      schedules: (course.schedules || []).reduce((result, schedule) => {
        if (isFileCourse(course)) {
          if (!syncStartDate || !syncEndDate) {
            return result;
          }

          const firstSessionDate = getFirstOccurrenceOnOrAfter(
            schedule.dayOfWeek,
            syncStartDate,
            syncEndDate,
          );

          if (!firstSessionDate) {
            return result;
          }

          result.push({
            ...schedule,
            startDate: firstSessionDate,
            endDate: syncEndDate,
          });

          return result;
        }

        if (syncMode !== "custom" || !syncStartDate || !syncEndDate) {
          result.push(schedule);
          return result;
        }

        if (
          schedule.startDate &&
          schedule.endDate &&
          schedule.startDate <= syncEndDate &&
          schedule.endDate >= syncStartDate
        ) {
          result.push({
            ...schedule,
            startDate:
              schedule.startDate < syncStartDate
                ? syncStartDate
                : schedule.startDate,
            endDate:
              schedule.endDate > syncEndDate ? syncEndDate : schedule.endDate,
          });
        }

        return result;
      }, []),
    }))
    .filter((course) => course.schedules.length > 0);

  return stripPreviewMetadata(nextCourses);
}

function formatDisplayDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return "";
  }

  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function buildPreviewEvent(course, schedule, color) {
  return {
    title: course.courseName,
    backgroundColor: color,
    borderColor: color,
    extendedProps: {
      room:
        schedule.room ||
        schedule.classroom ||
        schedule.location ||
        course.room ||
        "",
      courseKey: course.__courseKey,
      scheduleKey: schedule.__scheduleKey,
    },
  };
}

function buildPreviewEventDedupKey(event) {
  return [
    event.title,
    event.extendedProps?.courseKey || "",
    event.start || "",
    event.end || "",
    event.startTime || "",
    event.endTime || "",
    event.startRecur || "",
    event.endRecur || "",
    (event.daysOfWeek || []).join(","),
    event.extendedProps?.room || "",
  ].join("__");
}

function App() {
  const [step, setStep] = useState("welcome");
  const [previewCourses, setPreviewCourses] = useState([]);
  const [initialPreviewCourses, setInitialPreviewCourses] = useState([]);
  const [tokens, setTokens] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [calendarRangeLabel, setCalendarRangeLabel] = useState("");
  const [isCurrentWeek, setIsCurrentWeek] = useState(true);
  const [syncSummary, setSyncSummary] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [isSyncWindowVisible, setIsSyncWindowVisible] = useState(false);
  const [syncMode, setSyncMode] = useState("auto");
  const [syncStartDate, setSyncStartDate] = useState("");
  const [syncEndDate, setSyncEndDate] = useState("");
  const [initialCalendarDate, setInitialCalendarDate] = useState("");
  const calendarRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenData = params.get("tokens");

    if (tokenData) {
      try {
        const parsedTokens = JSON.parse(tokenData);
        setTokens(parsedTokens);
        localStorage.setItem("google_tokens", tokenData);
        setStep("source");
      } catch {
        localStorage.removeItem("google_tokens");
      }

      window.history.replaceState({}, document.title, "/");
    } else {
      const saved = localStorage.getItem("google_tokens");
      if (saved) {
        try {
          setTokens(JSON.parse(saved));
        } catch {
          localStorage.removeItem("google_tokens");
        }
      }
    }
  }, []);

  const resetPreviewState = () => {
    setPreviewCourses([]);
    setInitialPreviewCourses([]);
    setUndoSnapshot(null);
    setIsSyncWindowVisible(false);
    setSyncMode("auto");
    setSyncStartDate("");
    setSyncEndDate("");
    setInitialCalendarDate("");
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/auth/google-url`);
      window.location.href = res.data.url;
    } catch {
      alert("Không lấy được liên kết đăng nhập Google.");
    }
  };

  const handleLogout = () => {
    setTokens(null);
    setSyncSummary(null);
    resetPreviewState();
    localStorage.removeItem("google_tokens");
    setStep("welcome");
  };

  const handleEventsLoaded = (courses) => {
    const enrichedCourses = enrichCourses(courses);
    const { startDate, endDate } = getDateBounds(enrichedCourses);
    const currentWeekRange = getCurrentWeekRange();
    const shouldUseCustomRangeOnly = enrichedCourses.some(isFileCourse);

    setPreviewCourses(enrichedCourses);
    setInitialPreviewCourses(cloneCourses(enrichedCourses));
    setUndoSnapshot(null);
    setSyncMode(shouldUseCustomRangeOnly ? "custom" : "auto");
    setSyncStartDate(
      shouldUseCustomRangeOnly ? currentWeekRange.startDate : startDate,
    );
    setSyncEndDate(
      shouldUseCustomRangeOnly ? currentWeekRange.endDate : endDate,
    );
    setInitialCalendarDate(startDate || currentWeekRange.startDate);
    setIsSyncWindowVisible(false);
    setStep("preview");
  };

  const handleBack = () => {
    if (step === "preview") {
      resetPreviewState();
      setStep("source");
      return;
    }

    if (step === "success") {
      setSyncSummary(null);
      setStep("source");
      return;
    }

    setStep("welcome");
  };

  const handleDeleteSchedule = (courseKey, scheduleKey) => {
    setPreviewCourses((currentCourses) => {
      let deletedSnapshot = null;

      const nextCourses = currentCourses
        .map((course, courseIndex) => {
          if (course.__courseKey !== courseKey) {
            return course;
          }

          const scheduleIndex = course.schedules.findIndex(
            (schedule) => schedule.__scheduleKey === scheduleKey,
          );

          if (scheduleIndex === -1) {
            return course;
          }

          deletedSnapshot = {
            course: {
              ...course,
              schedules: [],
            },
            courseIndex,
            scheduleIndex,
            schedule: { ...course.schedules[scheduleIndex] },
          };

          return {
            ...course,
            schedules: course.schedules.filter(
              (schedule) => schedule.__scheduleKey !== scheduleKey,
            ),
          };
        })
        .filter((course) => course.schedules.length > 0);

      if (deletedSnapshot) {
        setUndoSnapshot(deletedSnapshot);
        setIsSyncWindowVisible(false);
      }

      return nextCourses;
    });
  };

  const handleUndoDelete = () => {
    if (!undoSnapshot) {
      return;
    }

    setPreviewCourses((currentCourses) => {
      const existingCourseIndex = currentCourses.findIndex(
        (course) => course.__courseKey === undoSnapshot.course.__courseKey,
      );

      if (existingCourseIndex >= 0) {
        return currentCourses.map((course, courseIndex) => {
          if (courseIndex !== existingCourseIndex) {
            return course;
          }

          const schedules = [...course.schedules];
          schedules.splice(
            undoSnapshot.scheduleIndex,
            0,
            undoSnapshot.schedule,
          );

          return {
            ...course,
            schedules,
          };
        });
      }

      const nextCourses = [...currentCourses];
      nextCourses.splice(undoSnapshot.courseIndex, 0, {
        ...undoSnapshot.course,
        schedules: [undoSnapshot.schedule],
      });
      return nextCourses;
    });

    setUndoSnapshot(null);
  };

  const handleUndoAllDeletes = () => {
    setPreviewCourses(cloneCourses(initialPreviewCourses));
    setUndoSnapshot(null);
    setIsSyncWindowVisible(false);
  };

  const syncPayload = useMemo(
    () =>
      buildSyncPayload(previewCourses, syncMode, syncStartDate, syncEndDate),
    [previewCourses, syncMode, syncStartDate, syncEndDate],
  );

  const isFilePreview = useMemo(
    () => previewCourses.some(isFileCourse),
    [previewCourses],
  );

  const calendarData = useMemo(() => {
    const dedupedEvents = [];
    const seenEvents = new Set();

    previewCourses.forEach((course) => {
      (course.schedules || []).forEach((schedule) => {
        const color = GOOGLE_COLORS_HEX[course.colorId] || GOOGLE_COLORS_HEX[1];

        const event =
          schedule.isDateInferred && schedule.previewStartDate
            ? {
                ...buildPreviewEvent(course, schedule, color),
                start: `${schedule.previewStartDate}T${schedule.startTime}:00`,
                end: `${schedule.previewStartDate}T${schedule.endTime}:00`,
              }
            : {
                ...buildPreviewEvent(course, schedule, color),
                startTime: schedule.startTime,
                endTime: schedule.endTime,
                startRecur: schedule.startDate,
                endRecur: schedule.endDate,
                daysOfWeek: [
                  dayOfWeekMap[normalizeText(schedule.dayOfWeek)] ?? 1,
                ],
              };

        const dedupKey = buildPreviewEventDedupKey(event);
        if (seenEvents.has(dedupKey)) {
          return;
        }

        seenEvents.add(dedupKey);
        dedupedEvents.push(event);
      });
    });

    return dedupedEvents;
  }, [previewCourses]);

  const previewEventCount = calendarData.length;
  const initialEventCount = countSchedules(initialPreviewCourses);
  const hasDeletedSchedules = previewEventCount < initialEventCount;
  const syncEventCount = countSchedules(syncPayload);
  const syncCourseCount = syncPayload.length;

  const handleOpenSyncWindow = () => {
    if (!tokens) {
      alert("Vui lòng liên kết Google trước khi đồng bộ lịch.");
      return;
    }

    if (!previewCourses.length || !previewEventCount) {
      alert("Chưa có buổi học nào để đồng bộ.");
      return;
    }

    setIsSyncWindowVisible(true);
  };

  const handleConfirmSync = async () => {
    if (syncMode === "custom") {
      if (!syncStartDate || !syncEndDate) {
        alert("Vui lòng chọn đầy đủ từ ngày và đến ngày.");
        return;
      }

      if (syncStartDate > syncEndDate) {
        alert("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
        return;
      }
    }

    if (!syncPayload.length || !syncEventCount) {
      alert("Không còn buổi học nào trong khoảng thời gian đã chọn.");
      return;
    }

    try {
      setIsSyncing(true);
      const res = await axios.post(`${API_BASE_URL}/api/calendar/sync`, {
        tokens,
        events: syncPayload,
      });

      const firstCalendarLink = res.data.calendarResults?.[0]?.htmlLink;
      setSyncSummary({
        eventCount: res.data.eventCount || 0,
        calendarLink:
          firstCalendarLink || "https://calendar.google.com/calendar/u/0/r",
      });
      resetPreviewState();
      setStep("success");
    } catch (err) {
      alert(
        `Đồng bộ lịch thất bại. ${
          err.response?.data?.message || err.message || "Vui lòng thử lại."
        }`,
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const renderCalendarEvent = (eventInfo) => {
    const room = eventInfo.event.extendedProps.room;
    const courseKey = eventInfo.event.extendedProps.courseKey;
    const scheduleKey = eventInfo.event.extendedProps.scheduleKey;

    return (
      <div className="calendar-event-content">
        <button
          type="button"
          className="calendar-event-delete"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleDeleteSchedule(courseKey, scheduleKey);
          }}
          aria-label={`Xóa buổi học ${eventInfo.event.title}`}
        >
          x
        </button>
        <div className="calendar-event-title">{eventInfo.event.title}</div>
        <div className="calendar-event-time">{eventInfo.timeText}</div>
        {room ? (
          <div className="calendar-event-room">
            <img
              src={locationIcon}
              alt=""
              className="calendar-event-room-icon"
            />
            <span>{room}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const formatRangeLabel = (start, endExclusive) => {
    const rangeEnd = new Date(endExclusive);
    rangeEnd.setDate(rangeEnd.getDate() - 1);

    const startDay = start.getDate();
    const startMonth = start.getMonth() + 1;
    const endDay = rangeEnd.getDate();
    const endMonth = rangeEnd.getMonth() + 1;
    const endYear = rangeEnd.getFullYear();

    return `${startDay} Th${startMonth} - ${endDay} Th${endMonth}, ${endYear}`;
  };

  const formatVietnameseWeekday = (date) => {
    const day = date.getDay();

    if (day === 0) {
      return "Chủ nhật";
    }

    return `Thứ ${day + 1}`;
  };

  const handleCalendarDatesSet = (dateInfo) => {
    setCalendarRangeLabel(formatRangeLabel(dateInfo.start, dateInfo.end));
    const today = new Date();
    setIsCurrentWeek(today >= dateInfo.start && today < dateInfo.end);
  };

  const handleCalendarNav = (action) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }

    if (action === "prev") {
      calendarApi.prev();
      return;
    }

    if (action === "next") {
      calendarApi.next();
      return;
    }

    calendarApi.today();
  };

  const handleOpenCalendar = () => {
    const targetUrl =
      syncSummary?.calendarLink || "https://calendar.google.com/calendar/u/0/r";
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  if (step === "welcome") {
    return (
      <div className="app-shell app-shell-welcome">
        <div className="welcome-card">
          <aside className="welcome-brand">
            <img src={qnuImage} alt="" className="welcome-brand-image" />
            <div className="welcome-brand-overlay" aria-hidden="true" />
          </aside>

          <section className="welcome-panel">
            <div className="welcome-panel-shell">
              <div
                className={`welcome-panel-inner${
                  tokens ? " is-connected" : ""
                }`}
              >
                <div className="welcome-panel-kicker">
                  <img
                    src={qnuSyncLogo}
                    alt="QNU Sync"
                    className="welcome-panel-logo"
                  />
                </div>
                <h2>Chào mừng quay trở lại</h2>
                <p className="panel-lead">
                  Đồng bộ thời khóa biểu QNU với Google Calendar theo cách nhanh
                  gọn và dễ theo dõi.
                </p>

                {!tokens ? (
                  <>
                    <button
                      className="google-btn"
                      onClick={handleConnectGoogle}
                    >
                      <span className="google-g" aria-hidden="true">
                        <svg viewBox="0 0 48 48" className="google-g-icon">
                          <path
                            fill="#FFC107"
                            d="M43.611 20.083H42V20H24v8h11.303C33.655 32.657 29.205 36 24 36c-6.627 0-12-5.373-12-12S17.373 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"
                          />
                          <path
                            fill="#FF3D00"
                            d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4c-7.682 0-14.41 4.337-17.694 10.691"
                          />
                          <path
                            fill="#4CAF50"
                            d="M24 44c5.168 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.144 35.091 26.686 36 24 36c-5.185 0-9.625-3.317-11.283-7.946l-6.522 5.025C9.435 39.556 16.227 44 24 44"
                          />
                          <path
                            fill="#1976D2"
                            d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.085 5.571l.003-.002l6.19 5.238C36.973 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"
                          />
                        </svg>
                      </span>
                      <span>Đăng nhập với Google</span>
                    </button>

                    <div className="security-note">
                      <span className="security-icon" aria-hidden="true">
                        <svg
                          width="14"
                          height="17"
                          viewBox="0 0 14 17"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.66667 16.6667C4.73611 16.1806 3.14236 15.0729 1.88542 13.3438C0.628472 11.6146 0 9.69444 0 7.58333V2.5L6.66667 0L13.3333 2.5V7.58333C13.3333 9.69444 12.7049 11.6146 11.4479 13.3438C10.191 15.0729 8.59722 16.1806 6.66667 16.6667ZM6.66667 14.9167C8.01389 14.5 9.13889 13.6771 10.0417 12.4479C10.9444 11.2188 11.4722 9.84722 11.625 8.33333H6.66667V1.77083L1.66667 3.64583V7.58333C1.66667 7.73611 1.66667 7.86111 1.66667 7.95833C1.66667 8.05556 1.68056 8.18056 1.70833 8.33333H6.66667V14.9167Z"
                            fill="#005BBF"
                          />
                        </svg>
                      </span>
                      <p>
                        Quyền riêng tư luôn được ưu tiên. Chúng tôi chỉ truy cập
                        cổng thông tin QNU để lấy thời khóa biểu của bạn.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="connected-banner">
                      <span>Tài khoản Google đã được liên kết</span>
                    </div>

                    <button
                      className="primary-large-btn"
                      onClick={() => setStep("source")}
                    >
                      Tiếp tục đồng bộ lịch học
                    </button>

                    <button className="ghost-btn" onClick={handleLogout}>
                      Đăng xuất
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (step === "source") {
    return (
      <div className="app-shell app-shell-source">
        <div className="topbar">
          <div />
          <button className="logout-chip" onClick={handleLogout}>
            Đăng xuất
          </button>
        </div>

        <header className="source-header">
          <h2>Đăng nhập QNU</h2>
          <p>
            Đăng nhập vào cổng thông tin sinh viên QNU để tự động lấy thời khóa
            biểu học kỳ hiện tại.
          </p>
        </header>

        <section className="source-grid">
          <WebScrapeTab
            onEventsLoaded={handleEventsLoaded}
            disabled={isSyncing}
          />
        </section>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="app-shell app-shell-success">
        <div className="success-card">
          <div className="preview-badge success-badge">
            <img src={tickIcon} alt="" className="preview-badge-icon" />
            <span>ĐỒNG BỘ THÀNH CÔNG</span>
          </div>
          <h2 className="success-title">
            Đã đồng bộ lịch học lên Google Calendar
          </h2>
          <p className="success-description">
            Lịch học đã được đồng bộ thành công. Bạn có thể quay lại hoặc mở
            Google Calendar để kiểm tra ngay.
          </p>

          <div className="success-summary">
            <img src={calendarIcon} alt="" className="success-summary-icon" />
            <span>
              {syncSummary?.eventCount || 0} sự kiện đã được tạo trên Google
              Calendar
            </span>
          </div>

          <div className="success-actions">
            <button
              className="cancel-btn success-secondary-btn"
              onClick={handleBack}
            >
              Quay lại
            </button>
            <button
              className="sync-btn success-primary-btn"
              onClick={handleOpenCalendar}
            >
              <span>Mở Google Calendar</span>
              <img src={externalIcon} alt="" className="success-action-icon" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`app-shell app-shell-preview${
        isSyncWindowVisible ? " is-sync-confirming" : ""
      }`}
    >
      <div className="preview-stage">
        <header className="preview-header">
          <div>
            <div className="preview-badge">
              <img src={tickIcon} alt="" className="preview-badge-icon" />
              <span>PHÂN TÍCH DỮ LIỆU THÀNH CÔNG</span>
            </div>
            <h2>Xem trước lịch học</h2>
            <p>
              Chúng tôi đã chuẩn bị các buổi học trong tuần từ thời khóa biểu
              của bạn để bạn xem trước.
            </p>
          </div>

          {!isFilePreview ? (
            <div className="preview-range-card">
              <button
                type="button"
                className={`today-pill${isCurrentWeek ? " is-disabled" : ""}`}
                onClick={() => handleCalendarNav("today")}
                disabled={isCurrentWeek}
              >
                Hôm nay
              </button>
              <button
                type="button"
                className="range-arrow"
                aria-label="Tuần trước"
                onClick={() => handleCalendarNav("prev")}
              >
                &#8249;
              </button>
              <span className="preview-range-label">{calendarRangeLabel}</span>
              <button
                type="button"
                className="range-arrow"
                aria-label="Tuần sau"
                onClick={() => handleCalendarNav("next")}
              >
                &#8250;
              </button>
            </div>
          ) : null}
        </header>

        <section
          className={`calendar-card${isFilePreview ? " is-file-preview" : ""}`}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin]}
            initialView="timeGridWeek"
            initialDate={initialCalendarDate || undefined}
            events={calendarData}
            datesSet={handleCalendarDatesSet}
            eventContent={renderCalendarEvent}
            slotEventOverlap={false}
            slotMinTime="07:00:00"
            slotMaxTime="18:00:00"
            allDaySlot={false}
            headerToolbar={false}
            height="auto"
            expandRows
            dayHeaderContent={(args) => (
              <div className="calendar-day-header">
                <span className="calendar-day-name">
                  {formatVietnameseWeekday(args.date)}
                </span>
                <span className="calendar-day-date">
                  {args.date.toLocaleDateString("vi-VN", {
                    day: "2-digit",
                    month: "2-digit",
                  })}
                </span>
              </div>
            )}
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
          />
        </section>

        {undoSnapshot ? (
          <div className="preview-undo-bar">
            <span>
              Đã xóa 1 buổi học của môn{" "}
              <strong>{undoSnapshot.course.courseName}</strong>.
            </span>
            <button type="button" onClick={handleUndoDelete}>
              Hoàn tác
            </button>
          </div>
        ) : null}

        <footer className="preview-actions">
          <div className="preview-actions-left">
            {hasDeletedSchedules ? (
              <button
                className="ghost-btn undo-all-btn"
                onClick={handleUndoAllDeletes}
              >
                Hoàn tác tất cả
              </button>
            ) : null}
          </div>
          <div className="preview-actions-right">
            <button className="cancel-btn" onClick={handleBack}>
              Hủy bỏ
            </button>
            <button
              className="sync-btn"
              onClick={handleOpenSyncWindow}
              disabled={isSyncing || !previewEventCount}
            >
              <span>
                {isSyncing ? "Đang đồng bộ..." : "Bắt đầu đồng bộ ngay"}
              </span>
              <img src={rocketIcon} alt="" className="sync-btn-icon" />
            </button>
          </div>
        </footer>
      </div>

      {isSyncWindowVisible ? (
        <div className="sync-window-overlay" role="dialog" aria-modal="true">
          <div className="sync-window-card">
            <h3>Khoảng thời gian đồng bộ</h3>
            <p>
              {isFilePreview
                ? "File bạn tải lên chưa có ngày bắt đầu và ngày kết thúc. Hãy chọn khoảng thời gian áp dụng trước khi đồng bộ lên Google Calendar. Mặc định là tuần hiện tại."
                : "Chọn cách đồng bộ theo toàn bộ thời khóa biểu hoặc giới hạn trong một khoảng ngày cụ thể trên Google Calendar."}
            </p>

            {!isFilePreview ? (
              <div className="sync-window-mode-group">
                <button
                  type="button"
                  className={`sync-mode-btn${syncMode === "auto" ? " is-active" : ""}`}
                  onClick={() => setSyncMode("auto")}
                >
                  Tự động
                </button>
                <button
                  type="button"
                  className={`sync-mode-btn${syncMode === "custom" ? " is-active" : ""}`}
                  onClick={() => setSyncMode("custom")}
                >
                  Theo khoảng thời gian
                </button>
              </div>
            ) : null}

            {isFilePreview || syncMode === "custom" ? (
              <div className="sync-window-date-grid">
                <label className="form-field">
                  <span>
                    Từ ngày <span className="required-mark">*</span>
                  </span>
                  <input
                    className="field-input"
                    type="date"
                    lang="vi-VN"
                    value={syncStartDate}
                    onChange={(event) => setSyncStartDate(event.target.value)}
                  />
                  {syncStartDate ? (
                    <span className="sync-date-display">
                      {formatDisplayDate(syncStartDate)}
                    </span>
                  ) : null}
                </label>

                <label className="form-field">
                  <span>
                    Đến ngày <span className="required-mark">*</span>
                  </span>
                  <input
                    className="field-input"
                    type="date"
                    lang="vi-VN"
                    value={syncEndDate}
                    onChange={(event) => setSyncEndDate(event.target.value)}
                  />
                  {syncEndDate ? (
                    <span className="sync-date-display">
                      {formatDisplayDate(syncEndDate)}
                    </span>
                  ) : null}
                </label>
              </div>
            ) : null}

            <div className="sync-window-summary">
              <strong>Sẽ đồng bộ lịch học từ {syncCourseCount} môn học.</strong>
              <p>
                {isFilePreview
                  ? "Bạn đang chọn khoảng ngày áp dụng cho lịch học được phân tích từ file."
                  : syncMode === "auto"
                    ? "Hệ thống sẽ dùng toàn bộ khoảng ngày đang có trong thời khóa biểu hiện tại."
                    : "Chỉ những buổi học nằm trong khoảng thời gian bạn chọn mới được đồng bộ."}
              </p>
            </div>

            <div className="sync-confirm-actions">
              <button
                type="button"
                className="cancel-btn sync-back-btn"
                onClick={() => setIsSyncWindowVisible(false)}
                disabled={isSyncing}
              >
                Quay lại
              </button>
              <button
                type="button"
                className="sync-btn"
                onClick={handleConfirmSync}
                disabled={isSyncing || !syncEventCount}
              >
                <span>
                  {isSyncing ? "Đang đồng bộ..." : "Xác nhận đồng bộ"}
                </span>
                <img src={rocketIcon} alt="" className="sync-btn-icon" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
